import { useMemo } from 'react'
import {
  ActivityLogIcon,
  ExclamationTriangleIcon,
  GlobeIcon,
  LapTimerIcon,
  LightningBoltIcon,
  PersonIcon,
  ReloadIcon,
} from '@radix-ui/react-icons'
import { PageHeader } from '@/components/layout'
import {
  Alert,
  AlertDescription,
  Badge,
  Button,
  Card,
  CardContent,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui'
import { formatDateTimeShort } from '@/lib/date-formatters'
import { formatInteger, formatPercent } from '@/lib/number-formatters'
import { useAssistantBridgeUsage } from './useAssistantBridgeUsage'

const DAY_OPTIONS = [
  { value: 7, label: '7d' },
  { value: 30, label: '30d' },
  { value: 60, label: '60d' },
  { value: 90, label: '90d' },
]

function formatDuration(value) {
  const duration = Number(value)

  if (!Number.isFinite(duration) || duration <= 0) {
    return '-'
  }

  if (duration < 1000) {
    return `${duration} ms`
  }

  return `${(duration / 1000).toFixed(1)} sec`
}

function formatList(values) {
  if (!Array.isArray(values) || values.length === 0) {
    return '-'
  }

  return values.join(', ')
}

function getActorIdentity(row) {
  return row?.actorEmail || row?.actorUserId || row?.actorHash || 'unknown'
}

function getActorIdentityPiiType(row) {
  if (row?.actorEmail) return 'email'
  if (row?.actorUserId) return 'user-id'
  return 'hash'
}

function errorRate(summary) {
  if (!summary.totalEvents) {
    return 0
  }

  return (Number(summary.errorEvents || 0) / Number(summary.totalEvents)) * 100
}

function buildAnswerStrip({ summary, platforms, tools, actors, recentEvents }) {
  const topPlatform = platforms[0]
  const topTool = tools[0]
  const topActor = actors[0]
  const recentPrompt = recentEvents.find((event) => event.queryLength || event.rawQuery)

  return [
    {
      title: 'Primary client',
      value: topPlatform?.platform || 'No client usage yet',
      detail: topPlatform
        ? `${formatInteger(topPlatform.toolCalls)} tool calls from ${formatInteger(topPlatform.activeActors)} actors`
        : 'No MCP events in this window.',
    },
    {
      title: 'Top capability',
      value: topTool?.toolName || 'No tool calls yet',
      detail: topTool
        ? `${formatInteger(topTool.callCount)} calls, ${formatInteger(topTool.errorCount)} errors`
        : 'Tool usage will appear after the first call.',
    },
    {
      title: 'Most active actor',
      value: topActor ? getActorIdentity(topActor) : 'No actor activity yet',
      detail: topActor
        ? `${formatInteger(topActor.toolCalls)} calls across ${formatList(topActor.platforms)}`
        : 'Actor identity appears after authenticated MCP usage.',
    },
    {
      title: 'Prompt visibility',
      value: recentPrompt?.rawQuery ? 'Raw prompts captured' : 'Privacy-safe summaries',
      detail: recentPrompt
        ? `${formatInteger(recentPrompt.queryLength, '-')} chars, hash ${recentPrompt.queryHash || '-'}`
        : `${formatInteger(summary.toolCalls)} tool calls checked for prompt summaries`,
    },
  ]
}

function MetricCard({ icon: Icon, label, value, detail, loading }) {
  return (
    <Card className="min-w-0 border-border/80">
      <CardContent className="min-w-0 px-5 py-4">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0 space-y-1">
            <p className="text-xs font-medium uppercase text-muted-foreground">{label}</p>
            {loading ? (
              <Skeleton className="h-8 w-24" />
            ) : (
              <p className="font-heading text-3xl font-semibold tabular-nums text-foreground">
                {value}
              </p>
            )}
          </div>
          <div className="grid size-10 shrink-0 place-items-center rounded-lg bg-accent text-accent-foreground">
            <Icon className="size-5" />
          </div>
        </div>
        {loading ? (
          <Skeleton className="mt-3 h-4 w-32" />
        ) : (
          <p className="mt-3 text-sm text-muted-foreground">{detail}</p>
        )}
      </CardContent>
    </Card>
  )
}

function AnswerCard({ answer, loading }) {
  return (
    <Card className="min-w-0 border-border/80">
      <CardContent className="min-w-0 space-y-2 px-5 py-4">
        {loading ? (
          <>
            <Skeleton className="h-4 w-28" />
            <Skeleton className="h-6 w-44" />
            <Skeleton className="h-4 w-full" />
          </>
        ) : (
          <>
            <p className="text-xs font-medium uppercase text-muted-foreground">{answer.title}</p>
            <p className="truncate text-lg font-semibold text-foreground" title={answer.value}>
              {answer.value}
            </p>
            <p className="line-clamp-2 break-words text-sm text-muted-foreground" title={answer.detail}>
              {answer.detail}
            </p>
          </>
        )}
      </CardContent>
    </Card>
  )
}

function SectionCard({ title, description, children }) {
  return (
    <Card className="min-w-0 border-border/80">
      <CardContent className="min-w-0 space-y-4 px-5 py-5">
        <div className="space-y-1">
          <h2 className="text-lg font-semibold text-foreground">{title}</h2>
          <p className="text-sm text-muted-foreground">{description}</p>
        </div>
        {children}
      </CardContent>
    </Card>
  )
}

function LoadingTable({ columns, rows = 5 }) {
  return (
    <div className="max-h-[60vh] min-w-0 overflow-auto">
      <Table className="min-w-[720px] table-fixed">
        <TableHeader>
          <TableRow>
            {columns.map((column) => (
              <TableHead key={column}>{column}</TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {Array.from({ length: rows }).map((_, rowIndex) => (
            <TableRow key={rowIndex}>
              {columns.map((column) => (
                <TableCell key={column}>
                  <Skeleton className="h-4 w-full max-w-[160px]" />
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  )
}

function InlineEmptyState({ label }) {
  return (
    <div className="rounded-lg border border-dashed border-border px-4 py-8 text-center text-sm text-muted-foreground">
      {label}
    </div>
  )
}

function SelectFilter({ value, onValueChange, options, placeholder }) {
  return (
    <Select value={value} onValueChange={onValueChange}>
      <SelectTrigger className="w-full sm:w-[180px]">
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="all">All</SelectItem>
        {options.map((option) => (
          <SelectItem key={option} value={option}>
            {option}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}

function PlatformTable({ rows, loading }) {
  if (loading) return <LoadingTable columns={['Client', 'Calls', 'Actors', 'Errors', 'Last seen']} />
  if (!rows.length) return <InlineEmptyState label="No platform usage in this scope." />

  return (
    <div className="max-h-[60vh] min-w-0 overflow-auto">
      <Table className="min-w-[720px] table-fixed">
        <TableHeader>
          <TableRow>
            <TableHead className="w-[220px]">Client</TableHead>
            <TableHead className="text-right">Calls</TableHead>
            <TableHead className="text-right">Actors</TableHead>
            <TableHead className="text-right">Errors</TableHead>
            <TableHead className="w-[180px]">Last seen</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={row.platform}>
              <TableCell>
                <Badge variant="secondary">{row.platform}</Badge>
              </TableCell>
              <TableCell className="text-right tabular-nums">
                {formatInteger(row.toolCalls)}
              </TableCell>
              <TableCell className="text-right tabular-nums">
                {formatInteger(row.activeActors)}
              </TableCell>
              <TableCell className="text-right tabular-nums">
                {formatInteger(row.errorEvents)}
              </TableCell>
              <TableCell>{formatDateTimeShort(row.lastSeenAt)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  )
}

function ToolTable({ rows, loading }) {
  if (loading) return <LoadingTable columns={['Tool', 'Calls', 'Actors', 'Errors', 'Returned', 'Last used']} />
  if (!rows.length) return <InlineEmptyState label="No tool calls in this scope." />

  return (
    <div className="max-h-[60vh] min-w-0 overflow-auto">
      <Table className="min-w-[780px] table-fixed">
        <TableHeader>
          <TableRow>
            <TableHead className="w-[260px]">Tool</TableHead>
            <TableHead className="text-right">Calls</TableHead>
            <TableHead className="text-right">Actors</TableHead>
            <TableHead className="text-right">Errors</TableHead>
            <TableHead className="text-right">Returned</TableHead>
            <TableHead className="w-[170px]">Last used</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={row.toolName}>
              <TableCell className="max-w-[260px] truncate font-mono text-xs" title={row.toolName}>
                {row.toolName}
              </TableCell>
              <TableCell className="text-right tabular-nums">{formatInteger(row.callCount)}</TableCell>
              <TableCell className="text-right tabular-nums">{formatInteger(row.activeActors)}</TableCell>
              <TableCell className="text-right tabular-nums">{formatInteger(row.errorCount)}</TableCell>
              <TableCell className="text-right tabular-nums">
                {formatInteger(row.dataRowsReturned)}
              </TableCell>
              <TableCell>{formatDateTimeShort(row.lastUsedAt)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  )
}

function ActorTable({ rows, loading }) {
  if (loading) return <LoadingTable columns={['Actor', 'Client', 'Calls', 'Scope', 'Top tool', 'Last seen']} />
  if (!rows.length) return <InlineEmptyState label="No identified actors in this scope." />

  return (
    <div className="max-h-[60vh] min-w-0 overflow-auto">
      <Table className="min-w-[900px] table-fixed">
        <TableHeader>
          <TableRow>
            <TableHead className="w-[260px]">Actor</TableHead>
            <TableHead className="w-[180px]">Client</TableHead>
            <TableHead className="text-right">Calls</TableHead>
            <TableHead className="text-right">Accounts</TableHead>
            <TableHead className="w-[220px]">Top tool</TableHead>
            <TableHead className="w-[170px]">Last seen</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={row.actorHash}>
              <TableCell>
                <div
                  className="max-w-[220px] truncate font-medium text-foreground"
                  title={getActorIdentity(row)}
                  data-pii={getActorIdentityPiiType(row)}
                >
                  {getActorIdentity(row)}
                </div>
                <div className="truncate text-xs text-muted-foreground" title={`${row.actorEmailDomain || '-'} / ${row.actorHash}`}>
                  <span data-pii="domain">{row.actorEmailDomain || '-'}</span>
                  {' / '}
                  <span data-pii="hash">{row.actorHash}</span>
                </div>
              </TableCell>
              <TableCell className="max-w-[180px] truncate">{formatList(row.platforms)}</TableCell>
              <TableCell className="text-right tabular-nums">{formatInteger(row.toolCalls)}</TableCell>
              <TableCell className="text-right tabular-nums">{formatInteger(row.accountCount)}</TableCell>
              <TableCell className="max-w-[220px] truncate font-mono text-xs" title={row.topToolName || '-'}>
                {row.topToolName || '-'}
              </TableCell>
              <TableCell>{formatDateTimeShort(row.lastSeenAt)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  )
}

function ClientTable({ rows, loading }) {
  if (loading) return <LoadingTable columns={['Client', 'Platform', 'Calls', 'Actors', 'Origin', 'Last seen']} />
  if (!rows.length) return <InlineEmptyState label="No client details in this scope." />

  return (
    <div className="max-h-[60vh] min-w-0 overflow-auto">
      <Table className="min-w-[880px] table-fixed">
        <TableHeader>
          <TableRow>
            <TableHead className="w-[220px]">Client</TableHead>
            <TableHead className="w-[150px]">Platform</TableHead>
            <TableHead className="text-right">Calls</TableHead>
            <TableHead className="text-right">Actors</TableHead>
            <TableHead className="w-[260px]">Origin</TableHead>
            <TableHead className="w-[170px]">Last seen</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={`${row.clientName}-${row.clientVersion}-${row.platform}`}>
              <TableCell>
                <div className="truncate font-medium text-foreground" title={row.clientName}>
                  {row.clientName}
                </div>
                <div className="truncate text-xs text-muted-foreground" title={row.clientVersion}>
                  {row.clientVersion}
                </div>
              </TableCell>
              <TableCell className="truncate" title={row.platform}>{row.platform}</TableCell>
              <TableCell className="text-right tabular-nums">{formatInteger(row.toolCalls)}</TableCell>
              <TableCell className="text-right tabular-nums">{formatInteger(row.activeActors)}</TableCell>
              <TableCell className="max-w-[260px] truncate" title={row.sampleOrigin || row.sampleHost || '-'}>
                {row.sampleOrigin || row.sampleHost || '-'}
              </TableCell>
              <TableCell>{formatDateTimeShort(row.lastSeenAt)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  )
}

function RecentEventsTable({ rows, loading }) {
  if (loading) return <LoadingTable columns={['Time', 'Actor', 'Tool', 'Prompt', 'Result', 'Latency']} rows={8} />
  if (!rows.length) return <InlineEmptyState label="No recent MCP events in this scope." />

  return (
    <div className="max-h-[70vh] min-w-0 overflow-auto">
      <Table className="min-w-[1180px] table-fixed">
        <TableHeader>
          <TableRow>
            <TableHead className="w-[160px]">Time</TableHead>
            <TableHead className="w-[230px]">Actor</TableHead>
            <TableHead className="w-[220px]">Tool</TableHead>
            <TableHead className="w-[300px]">Prompt</TableHead>
            <TableHead className="w-[220px]">Result</TableHead>
            <TableHead className="w-[110px] text-right">Latency</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={row.id}>
              <TableCell className="whitespace-nowrap">{formatDateTimeShort(row.createdAt)}</TableCell>
              <TableCell>
                <div
                  className="max-w-[180px] truncate text-sm font-medium"
                  title={getActorIdentity(row)}
                  data-pii={getActorIdentityPiiType(row)}
                >
                  {getActorIdentity(row)}
                </div>
                <div className="truncate text-xs text-muted-foreground" title={`${row.platform} / ${row.actorHash}`}>
                  {row.platform} / <span data-pii="hash">{row.actorHash}</span>
                </div>
              </TableCell>
              <TableCell className="max-w-[220px] truncate font-mono text-xs" title={row.toolName}>
                {row.toolName}
              </TableCell>
              <TableCell className="max-w-[320px]">
                {row.rawQuery ? (
                  <span className="line-clamp-2 break-words text-sm" data-pii="content">
                    {row.rawQuery}
                  </span>
                ) : (
                  <span className="line-clamp-2 break-words text-sm text-muted-foreground">
                    {formatInteger(row.queryLength, '-')} chars / {row.queryHash || 'no hash'}
                  </span>
                )}
                <div className="mt-1 flex flex-wrap gap-1">
                  {row.hasContext && <Badge variant="outline">context</Badge>}
                  {row.hasConversationContext && <Badge variant="outline">conversation</Badge>}
                </div>
              </TableCell>
              <TableCell>
                <div className="flex flex-wrap gap-1">
                  <Badge variant={row.outcome === 'error' ? 'destructive' : 'secondary'}>
                    {row.outcome}
                  </Badge>
                  {row.partialFailures > 0 && <Badge variant="outline">partial</Badge>}
                </div>
                <div className="mt-1 text-xs text-muted-foreground">
                  {formatInteger(row.dataCount)} rows / {formatInteger(row.responseTextLength)} chars
                </div>
                {row.responseTextPreview && (
                  <div
                    className="mt-1 line-clamp-2 break-words text-xs text-muted-foreground"
                    title={row.responseTextPreview}
                    data-pii="content"
                  >
                    {row.responseTextPreview}
                  </div>
                )}
              </TableCell>
              <TableCell className="text-right tabular-nums">{formatDuration(row.durationMs)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  )
}

export function AssistantBridgeUsagePage() {
  const {
    daysBack,
    setDaysBack,
    platform,
    setPlatform,
    profile,
    setProfile,
    toolName,
    setToolName,
    actorHash,
    setActorHash,
    summary,
    instrumentation,
    platforms,
    tools,
    actors,
    clients,
    recentEvents,
    profiles,
    options,
    generatedAt,
    isLoading,
    isFetching,
    isError,
    error,
    refetch,
  } = useAssistantBridgeUsage()

  const answers = useMemo(
    () => buildAnswerStrip({ summary, platforms, tools, actors, recentEvents }),
    [summary, platforms, tools, actors, recentEvents],
  )

  return (
    <div className="flex h-full flex-col">
      <PageHeader
        title="MCP usage"
        description="Usage, actors, clients, prompts, and tool health for AssistantBridge MCP."
      >
        <Button variant="outline" onClick={() => refetch()} disabled={isFetching}>
          <ReloadIcon className="mr-2 h-4 w-4" />
          Refresh
        </Button>
      </PageHeader>

      <main className="flex-1 space-y-6 overflow-y-auto p-6">
        <section className="flex flex-wrap items-center gap-3">
          <Select value={String(daysBack)} onValueChange={(value) => setDaysBack(Number(value))}>
            <SelectTrigger className="w-full sm:w-[110px]">
              <SelectValue placeholder="Window" />
            </SelectTrigger>
            <SelectContent>
              {DAY_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={String(option.value)}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <SelectFilter
            value={platform}
            onValueChange={setPlatform}
            options={options.platforms}
            placeholder="Client"
          />
          <SelectFilter
            value={profile}
            onValueChange={setProfile}
            options={options.profiles}
            placeholder="Profile"
          />
          <SelectFilter
            value={toolName}
            onValueChange={setToolName}
            options={options.tools}
            placeholder="Tool"
          />
          <Input
            value={actorHash}
            onChange={(event) => setActorHash(event.target.value)}
            placeholder="Actor hash"
            className="w-full sm:w-[220px]"
            data-pii="hash"
          />
          {generatedAt && (
            <span className="ml-auto shrink-0 text-xs text-muted-foreground">
              Updated {formatDateTimeShort(generatedAt)}
            </span>
          )}
        </section>

        {isError && (
          <Alert variant="destructive">
            <ExclamationTriangleIcon className="h-4 w-4" />
            <AlertDescription>
              {error?.message || 'AssistantBridge usage could not be loaded.'}
            </AlertDescription>
          </Alert>
        )}

        <section className="grid gap-4 xl:grid-cols-5">
          <MetricCard
            icon={LightningBoltIcon}
            label="Tool calls"
            value={formatInteger(summary.toolCalls)}
            detail={`${formatInteger(summary.totalEvents)} total MCP events`}
            loading={isLoading}
          />
          <MetricCard
            icon={PersonIcon}
            label="Actors"
            value={formatInteger(summary.activeActors)}
            detail={`${formatInteger(summary.activeTokens)} active tokens`}
            loading={isLoading}
          />
          <MetricCard
            icon={GlobeIcon}
            label="Platforms"
            value={formatInteger(summary.activePlatforms)}
            detail={`${formatInteger(summary.activeAccounts)} account scopes`}
            loading={isLoading}
          />
          <MetricCard
            icon={ActivityLogIcon}
            label="Error rate"
            value={formatPercent(errorRate(summary))}
            detail={`${formatInteger(summary.errorEvents)} errored events`}
            loading={isLoading}
          />
          <MetricCard
            icon={LapTimerIcon}
            label="P95 latency"
            value={formatDuration(summary.p95DurationMs)}
            detail={`Average ${formatDuration(summary.avgDurationMs)}`}
            loading={isLoading}
          />
        </section>

        <section className="grid gap-4 xl:grid-cols-4">
          {answers.map((answer) => (
            <AnswerCard key={answer.title} answer={answer} loading={isLoading} />
          ))}
        </section>

        <section className="grid gap-4 xl:grid-cols-[1fr_1fr]">
          <SectionCard
            title="Client platform mix"
            description="Which assistant surfaces are driving MCP usage."
          >
            <PlatformTable rows={platforms} loading={isLoading} />
          </SectionCard>
          <SectionCard
            title="Tool adoption"
            description="Which MCP capabilities are being called, and whether they are succeeding."
          >
            <ToolTable rows={tools} loading={isLoading} />
          </SectionCard>
        </section>

        <section className="grid gap-4 xl:grid-cols-[1fr_1fr]">
          <SectionCard
            title="Actor activity"
            description="Stable actor hashes, domains, client surfaces, and account scope."
          >
            <ActorTable rows={actors} loading={isLoading} />
          </SectionCard>
          <SectionCard
            title="Client details"
            description="Client names, versions, origins, hosts, and inferred platforms."
          >
            <ClientTable rows={clients} loading={isLoading} />
          </SectionCard>
        </section>

        <SectionCard
          title="Recent prompt and tool events"
          description="Latest tool calls and failures with prompt summaries, response size, status, and latency."
        >
          <div className="mb-3 flex flex-wrap gap-2">
            <Badge variant={instrumentation.rawToolArgumentsEnabled ? 'secondary' : 'outline'}>
              Raw prompts {instrumentation.rawToolArgumentsEnabled ? 'on' : 'off'}
            </Badge>
            <Badge variant={instrumentation.responsePreviewEnabled ? 'secondary' : 'outline'}>
              Response previews {instrumentation.responsePreviewEnabled ? 'on' : 'off'}
            </Badge>
            {instrumentation.dbLoggingDisabled && <Badge variant="destructive">DB logging off</Badge>}
            {instrumentation.readonlyContainer && <Badge variant="destructive">Readonly container</Badge>}
          </div>
          <RecentEventsTable rows={recentEvents} loading={isLoading} />
        </SectionCard>

        <SectionCard
          title="Profile and role mix"
          description="Usage split by AssistantBridge profile and role."
        >
          {isLoading ? (
            <LoadingTable columns={['Profile', 'Role', 'Calls', 'Actors', 'Last seen']} />
          ) : profiles.length ? (
            <div className="max-h-[50vh] min-w-0 overflow-auto">
              <Table className="min-w-[680px] table-fixed">
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[180px]">Profile</TableHead>
                    <TableHead className="w-[180px]">Role</TableHead>
                    <TableHead className="text-right">Calls</TableHead>
                    <TableHead className="text-right">Actors</TableHead>
                    <TableHead className="w-[170px]">Last seen</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {profiles.map((row) => (
                    <TableRow key={`${row.profile}-${row.role}`}>
                      <TableCell>{row.profile}</TableCell>
                      <TableCell>{row.role}</TableCell>
                      <TableCell className="text-right tabular-nums">
                        {formatInteger(row.toolCalls)}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {formatInteger(row.activeActors)}
                      </TableCell>
                      <TableCell>{formatDateTimeShort(row.lastSeenAt)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <InlineEmptyState label="No profile usage in this scope." />
          )}
        </SectionCard>
      </main>
    </div>
  )
}

export default AssistantBridgeUsagePage
